import { Input } from '@components/core-ui/form-elements/Input';
import { DateRangePicker } from '@components/core-ui/form-elements/DateRangePicker';
import { SearchableSelect } from '@components/core-ui/form-elements/SearchableSelect';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { format } from 'date-fns';
import { parseStartOfDayFromQueryParam } from '@helpers/parseStartOfDayFromQueryParam';
import { useId } from 'react';

import { AUDIT_MAX_FILTER_IDS, parseAuditIdList } from '../../../../helpers/isAuditUuid';
import { TProps } from './types/TProps';

const parseCreatedDate = (value: string): Date | null => parseStartOfDayFromQueryParam(value) ?? null;

const idListError = (text: string): string | null => {
  const { invalid, tooMany } = parseAuditIdList(text);
  if (invalid.length === 1) {
    return `Not a valid ID: ${invalid[0]}`;
  }
  if (invalid.length > 1) {
    return `Not valid IDs: ${invalid.join(', ')}`;
  }
  if (tooMany) {
    return `Enter at most ${AUDIT_MAX_FILTER_IDS} IDs`;
  }
  return null;
};

type TIdFilterProps = {
  name: 'entityId' | 'actorUserId';
  label: string;
  value: string;
  onChange: (value: string) => void;
};

const IdFilter = ({ name, label, value, onChange }: TIdFilterProps) => {
  const errorId = useId();
  const error = idListError(value);

  return (
    <div className="flex flex-col gap-1">
      <Input
        name={name}
        label={label}
        placeholder="One or more IDs, comma separated"
        value={value}
        error={error !== null}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (!/[\r\n]/.test(pasted)) {
            return;
          }
          event.preventDefault();
          const input = event.currentTarget;
          const start = input.selectionStart ?? value.length;
          const end = input.selectionEnd ?? value.length;
          const normalized = pasted.replace(/\s*[\r\n]+\s*/g, ', ');
          onChange(`${value.slice(0, start)}${normalized}${value.slice(end)}`);
        }}
      />
      {error ? (
        <p id={errorId} className="break-all text-xs text-[var(--color-form-field-error)]">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export const Filters = ({
  entityType,
  entityId,
  actorType,
  businessAction,
  actorUserId,
  actorLabel,
  createdFrom,
  createdTo,
  entityTypeOptions,
  actorTypeOptions,
  businessActionOptions,
  hasActiveFilters,
  onCreatedRangeChange,
  onChange,
  onListChange,
  onClear,
}: TProps) => {
  const createdRange: [Date | null, Date | null] = [parseCreatedDate(createdFrom), parseCreatedDate(createdTo)];

  return (
    <section className="mb-4 rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg font-medium text-foreground">Filters</h2>
        <TextButton className="!w-auto" disabled={!hasActiveFilters} onClick={onClear}>
          Clear filters
        </TextButton>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SearchableSelect
          multiple
          label="Entity type"
          placeholder="All entity types"
          value={entityType}
          options={entityTypeOptions.map((option) => ({ value: option, label: option }))}
          onChange={(values: string[]) => onListChange('entityType', values)}
        />

        <IdFilter
          name="entityId"
          label="Entity id"
          value={entityId}
          onChange={(value) => onChange('entityId', value)}
        />

        <SearchableSelect
          multiple
          label="Actor type"
          placeholder="All actor types"
          value={actorType}
          options={actorTypeOptions.map((option) => ({ value: option, label: option }))}
          onChange={(values: string[]) => onListChange('actorType', values)}
        />

        <SearchableSelect
          multiple
          label="Business action"
          placeholder="All business actions"
          value={businessAction}
          options={businessActionOptions.map((option) => ({ value: option, label: option }))}
          onChange={(values: string[]) => onListChange('businessAction', values)}
        />

        <IdFilter
          name="actorUserId"
          label="Actor user id"
          value={actorUserId}
          onChange={(value) => onChange('actorUserId', value)}
        />

        <Input
          name="actorLabel"
          label="Actor"
          placeholder="Email, credential or job"
          value={actorLabel}
          onChange={(event) => onChange('actorLabel', event.target.value)}
        />

        <DateRangePicker
          label="Created"
          value={createdRange}
          maxDate={new Date()}
          onChange={([from, to]: [Date | null, Date | null]) =>
            onCreatedRangeChange(from ? format(from, 'yyyy-MM-dd') : '', to ? format(to, 'yyyy-MM-dd') : '')
          }
        />
      </div>
    </section>
  );
};
